import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, useSession } from '@prooffleet/client';
import { hasCapability } from '@prooffleet/contracts';
import { severityColor } from '@prooffleet/ui';
import { EvidencePhoto } from '../components/EvidencePhoto.js';
import { RepairPhotos, type UploadedPhoto } from '../components/RepairPhotos.js';
import { EmptyState, PageHeader } from '../components/Page.js';

interface DefectRow {
  _id: string;
  unitId: string;
  label: string;
  severity: string;
  status: string;
  createdAt: string;
  measurement: { value: number; threshold: number | null } | null;
  photos: { objectKey: string; versionId: string | null }[];
}

interface UnitRow {
  _id: string;
  unitNumber: string;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged by driver',
  assigned: 'Assigned',
  repaired: 'Repaired, awaiting verification',
  verified: 'Verified',
  closed: 'Closed',
};

/** Доска дефектов по всем юнитам организации. */
export function Defects(): React.JSX.Element {
  const [severity, setSeverity] = useState('all');

  const { data: units } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: UnitRow[] }>('/units'),
  });

  const unitIds = (units?.units ?? []).map((u) => u._id);

  const { data } = useQuery({
    queryKey: ['open-defects', unitIds],
    enabled: unitIds.length > 0,
    queryFn: async () => {
      const perUnit = await Promise.all(
        unitIds.map(async (id) => {
          const result = await api<{ defects: DefectRow[] }>(`/units/${id}/open-defects`);
          return result.defects.map((d) => ({ ...d, unitId: id }));
        }),
      );
      return perUnit.flat();
    },
    refetchInterval: 30_000,
  });

  const unitNumber = new Map((units?.units ?? []).map((u) => [u._id, u.unitNumber]));

  const actor = useSession((st) => st.actor);
  const canAssign = actor !== null && hasCapability(actor, 'defect:assign');
  const canClose = actor !== null && hasCapability(actor, 'defect:close');
  const queryClient = useQueryClient();

  const [assignTarget, setAssignTarget] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [assignNote, setAssignNote] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);

  const [workTarget, setWorkTarget] = useState<{ id: string; unitId: string; mode: 'repair' | 'close' } | null>(null);
  const [workPhotos, setWorkPhotos] = useState<UploadedPhoto[]>([]);
  const [workNote, setWorkNote] = useState('');
  const [workError, setWorkError] = useState<string | null>(null);

  const { data: people } = useQuery({
    queryKey: ['users'],
    enabled: canAssign,
    queryFn: () =>
      api<{ members: { userId: string; displayName: string; roleName: string }[] }>('/users'),
  });

  const work = useMutation({
    mutationFn: () => {
      if (workTarget === null) throw new Error('no target');
      const path = workTarget.mode === 'repair' ? 'repair' : 'close';
      return api(`/defects/${workTarget.id}/${path}`, {
        method: 'POST',
        body: { photos: workPhotos, note: workNote === '' ? null : workNote },
      });
    },
    onSuccess: async () => {
      setWorkTarget(null);
      setWorkPhotos([]);
      setWorkNote('');
      setWorkError(null);
      await queryClient.invalidateQueries({ queryKey: ['open-defects'] });
    },
    onError: (err: unknown) => {
      setWorkError(err instanceof ApiError ? err.message : 'Could not save');
    },
  });

  const assign = useMutation({
    mutationFn: () =>
      api(`/defects/${assignTarget ?? ''}/assign`, {
        method: 'POST',
        body: { assigneeUserId: assignee, note: assignNote === '' ? null : assignNote },
      }),
    onSuccess: async () => {
      setAssignTarget(null);
      setAssignError(null);
      await queryClient.invalidateQueries({ queryKey: ['open-defects'] });
    },
    onError: (err: unknown) => {
      setAssignError(err instanceof ApiError ? err.message : 'Could not assign the defect');
    },
  });
  const defects = (data ?? []).filter((d) => severity === 'all' || d.severity === severity);

  return (
    <Stack gap={0}>
      <PageHeader
        title="Defects"
        count={defects.length}
        hint="Open defects across the fleet. A defect closes only with a photo of the completed work."
        action={
          <SegmentedControl
            size="xs"
            value={severity}
            onChange={setSeverity}
            data={[
              { value: 'all', label: 'All' },
              { value: 'critical', label: 'Critical' },
              { value: 'major', label: 'Major' },
              { value: 'minor', label: 'Minor' },
            ]}
          />
        }
      />

      {defects.length === 0 && (
        <EmptyState
          tone={severity === 'all' ? 'good' : 'neutral'}
          title={severity === 'all' ? 'No open defects' : `No open ${severity} defects`}
          hint={
            severity === 'all'
              ? 'Every reported defect has been repaired and verified.'
              : 'Switch the filter to see defects at other severities.'
          }
        />
      )}

      <Stack gap={0}>
        {defects.map((defect) => (
          <div
            key={defect._id}
            className={`pf-stripe pf-stripe-${
              defect.severity === 'critical' ? 'critical' : defect.severity === 'major' ? 'warn' : 'ok'
            }`}
            style={{ borderBottom: '1px solid var(--pf-line)', padding: '14px 16px' }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
              <Group align="flex-start" wrap="nowrap" gap="md" style={{ minWidth: 0 }}>
                {defect.photos[0] !== undefined && (
                  <div style={{ width: 92, flex: '0 0 92px' }}>
                    <EvidencePhoto
                      objectKey={defect.photos[0].objectKey}
                      versionId={defect.photos[0].versionId}
                      height={62}
                    />
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <Group gap={8} align="baseline" wrap="nowrap">
                    <Link
                      to={`/units/${defect.unitId}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Text fw={660} fz="sm">
                        {unitNumber.get(defect.unitId) ?? '—'}
                      </Text>
                    </Link>
                    <Text fz="sm" c="gray.7" truncate>
                      {defect.label}
                    </Text>
                  </Group>

                  {defect.measurement !== null && (
                    // Замер — суть дефекта, поэтому он крупнее подписи и моноширинный.
                    <Group gap={6} align="baseline" mt={4}>
                      <Text className="pf-num" fw={620} fz="md">
                        {defect.measurement.value}
                      </Text>
                      {defect.measurement.threshold !== null && (
                        <Text fz="xs" c="dimmed" className="pf-num">
                          limit {defect.measurement.threshold}
                        </Text>
                      )}
                    </Group>
                  )}

                  <Text className="pf-mono" mt={4}>
                    {new Date(defect.createdAt).toLocaleString()}
                  </Text>
                </div>
              </Group>

              <Group gap={8} wrap="nowrap" style={{ flex: '0 0 auto' }} align="center">
                <Badge color={severityColor(defect.severity)} size="sm">
                  {defect.severity}
                </Badge>
                <Text fz="xs" c="dimmed">
                  {STATUS_LABEL[defect.status] ?? defect.status}
                </Text>
                {defect.status !== 'closed' && (
                  <Group gap={4} wrap="nowrap" className="pf-row-action">
                    {canAssign && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => {
                          setAssignTarget(defect._id);
                          setAssignee(null);
                          setAssignNote('');
                          setAssignError(null);
                        }}
                      >
                        {defect.status === 'assigned' ? 'Reassign' : 'Assign'}
                      </Button>
                    )}
                    {canClose && defect.status !== 'repaired' && (
                      <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => {
                          setWorkTarget({ id: defect._id, unitId: defect.unitId, mode: 'repair' });
                          setWorkPhotos([]);
                          setWorkNote('');
                          setWorkError(null);
                        }}
                      >
                        Mark repaired
                      </Button>
                    )}
                    {canClose && defect.status === 'repaired' && (
                      <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => {
                          setWorkTarget({ id: defect._id, unitId: defect.unitId, mode: 'close' });
                          setWorkPhotos([]);
                          setWorkNote('');
                          setWorkError(null);
                        }}
                      >
                        Verify and close
                      </Button>
                    )}
                  </Group>
                )}
              </Group>
            </Group>
          </div>
        ))}
      </Stack>

      <Modal
        opened={assignTarget !== null}
        onClose={() => setAssignTarget(null)}
        title="Assign the repair"
      >
        <Stack gap="md">
          <Text c="dimmed" fz="sm">
            Assignment is a record, not a field: who handed the repair over and when stays in the
            defect history and in the evidence package.
          </Text>
          <Select
            label="Assign to"
            searchable
            data={(people?.members ?? []).map((m) => ({
              value: m.userId,
              label: `${m.displayName} · ${m.roleName}`,
            }))}
            value={assignee}
            onChange={setAssignee}
          />
          <Textarea
            label="Note"
            autosize
            minRows={2}
            value={assignNote}
            onChange={(e) => setAssignNote(e.currentTarget.value)}
          />
          {assignError !== null && <Alert color="red">{assignError}</Alert>}
          <Button
            loading={assign.isPending}
            disabled={assignee === null}
            onClick={() => assign.mutate()}
          >
            Assign
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={workTarget !== null}
        onClose={() => setWorkTarget(null)}
        title={workTarget?.mode === 'repair' ? 'Mark the repair done' : 'Verify and close'}
      >
        <Stack gap="md">
          {workTarget?.mode === 'repair' ? (
            <Text c="dimmed" fz="sm">
              A photo of the completed work is required. Without it &quot;repaired&quot; is the same
              checkbox this product exists to replace.
            </Text>
          ) : (
            <Alert color="blue">
              The repair photo is already on record, so nothing else is required here. Closing is the
              act of accepting it — ideally done by someone other than whoever did the work.
            </Alert>
          )}

          {workTarget !== null && (
            <RepairPhotos
              unitId={workTarget.unitId}
              photos={workPhotos}
              onChange={setWorkPhotos}
            />
          )}

          <Textarea
            label="Note"
            autosize
            minRows={2}
            value={workNote}
            onChange={(e) => setWorkNote(e.currentTarget.value)}
          />

          {workError !== null && <Alert color="red">{workError}</Alert>}

          <Button
            loading={work.isPending}
            disabled={workTarget?.mode === 'repair' && workPhotos.length === 0}
            onClick={() => work.mutate()}
          >
            {workTarget?.mode === 'repair' ? 'Mark repaired' : 'Close defect'}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
